import React from 'react'

import { Controller, useFormContext } from 'react-hook-form'

import { ArcadeInput, Input as DefaultInput } from '@cash-design-system/react'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-feature-flags'

import { FieldErrorText, FormInputWrapper } from '../'
import { FieldController } from './FieldController/FieldController'

type Props = React.ComponentProps<typeof DefaultInput | typeof ArcadeInput> & {
  transform?: (value: string) => unknown
  name: string
  rules?: React.ComponentProps<typeof Controller>['rules'] // https://react-hook-form.com/api/useform/register#options
  customInput?: React.ComponentType<React.ComponentProps<typeof DefaultInput>>
  showErrors?: boolean
}

export function FormInput({
  name,
  rules,
  customInput,
  showErrors = true,
  transform = v => v,
  defaultValue,
  ...props
}: Props): React.ReactElement {
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')
  const { register, formState, getFieldState } = useFormContext()

  if (isArcadeMigrationEnabled) {
    const Input = customInput ?? ArcadeInput
    return (
      <FormInputWrapper>
        <FieldController
          FieldComponent={Input}
          componentProps={{
            ...props,
            id: props.id ?? name,
          }}
          defaultValue={props.value || defaultValue}
          name={name}
          rules={rules}
          transform={transform}
        />
      </FormInputWrapper>
    )
  }

  const { isSubmitting, isSubmitted } = formState

  const { isDirty, error } = getFieldState(name, formState)

  const { onBlur, onChange, ...inputProps } = register(name, {
    ...rules,
    setValueAs: transform,
    value: props.value ?? defaultValue,
  })

  const showError = error && (error?.type !== 'required' || isSubmitted)

  const Input = customInput ?? DefaultInput
  return (
    <FormInputWrapper>
      <Input
        {...props}
        {...inputProps}
        disabled={props.disabled || isSubmitting}
        onChange={e => {
          props.onChange?.(e)
          onChange(e)
        }}
        onBlur={e => {
          props.onBlur?.(e)
          if (isDirty) {
            // validate onBlur only when field is dirty
            onBlur(e)
          }
        }}
        id={props.id ?? name}
        className={showError ? 'error' : ''}
      />
      {showErrors && <FieldErrorText name={name} />}
    </FormInputWrapper>
  )
}
